import { Body } from '@assembly-js/design-system';
import { IconButton, useMediaQuery, useTheme } from '@mui/material';
import type { ICellRendererParams } from 'ag-grid-community';
import React from 'react';

import { ExternalLinkIcon } from 'src/components/icons';
import {
  QuickTextEditor,
  QuickTextEditorProps,
} from 'src/components/QuickTextEditor';
import { COPYABLE_FIELDS_TYPES } from 'src/constants';
import AddressInput from 'src/features/crm/properties/AddressInput';
import { MapFieldTypeToDefaultProps } from 'src/features/crm/properties/MapFieldTypeToDefaultProps';
import {
  CompanyWithClients,
  CustomFieldValue,
  CustomFieldValueData,
  isAddressCustomFieldValues,
} from 'src/features/crm/types/type';
// TODO: will move out of legacy in the future because it depends on other legacy components
/* eslint-disable no-restricted-imports */
import { MultiSelect } from 'src/legacy/components/MultiSelect';
import { MultiSelectInput } from 'src/legacy/components/MultiSelect/MultiSelectInput';
import { MultiSelectOption } from 'src/legacy/components/Select/types';
/* eslint-enable no-restricted-imports */
import {
  AddressCustomField,
  ClientTableRowModel,
  CustomFieldOption,
  isAddressCustomField,
  isMultiSelectCustomField,
  MultiSelectCustomField,
} from 'src/store/clients/types';
import { UrlUtils } from 'src/utils';
import {
  formatFieldValue,
  getFieldInputComponent,
  getFieldValidationByType,
} from 'src/utils/CustomFieldsUtils';

type CustomFieldsType = {
  [key: string]: CustomFieldValue | null | undefined;
};
export interface CustomFieldsRendererProps extends ICellRendererParams {
  customFieldData: MultiSelectCustomField | CustomFieldOption;
  data: (ClientTableRowModel | CompanyWithClients) & CustomFieldsType;
  setIsEdit?: (isEdit: boolean) => void;
  onCustomFieldValueUpdated: (customFieldData: CustomFieldValueData) => void;
  onCustomFieldPropertyUpdated: (
    customFieldProperty: CustomFieldOption,
    deletedOptionKey?: string,
  ) => Promise<void>;
}
export const CustomFieldsRenderer: React.FC<CustomFieldsRendererProps> =
  React.memo(
    ({
      customFieldData: field,
      onCustomFieldValueUpdated,
      onCustomFieldPropertyUpdated,
      data,
      setIsEdit = () => null,
    }) => {
      const theme = useTheme();
      const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
      const { FieldIcon, inputComponent } = React.useMemo(
        () => ({
          FieldIcon: MapFieldTypeToDefaultProps[field.type].icon,
          inputComponent: getFieldInputComponent(field.type),
        }),
        [field.type],
      );
      const [editable, setEditable] = React.useState(false);
      const cellValue = React.useMemo(() => data[field.id], [data, field.id]);

      const multiSelectOptionIds = React.useMemo(() => {
        if (field.type !== 'multiSelect' || !Array.isArray(cellValue)) {
          return [] as string[];
        }

        return cellValue;
      }, [cellValue, field.type]);

      const multiSelectOptions = React.useMemo(
        () =>
          field.type === 'multiSelect' && isMultiSelectCustomField(field)
            ? field.options
            : [],
        [field],
      );
      const multiSelectOptionsById = React.useMemo(
        () =>
          new Map(
            multiSelectOptions.map((option) => [option.id, option] as const),
          ),
        [multiSelectOptions],
      );

      const selectedTags = React.useMemo(() => {
        return multiSelectOptionIds.reduce<MultiSelectOption[]>(
          (tags, tagId) => {
            const selectedTag = multiSelectOptionsById.get(tagId);
            if (selectedTag) {
              tags.push(selectedTag);
            }
            return tags;
          },
          [],
        );
      }, [multiSelectOptionIds, multiSelectOptionsById]);

      /**
       * This function handle saving new custom field value
       * @param newValue custom field value to submit
       */
      // Type guards to check the structure of the data
      const isClientTableRowModel = (
        item: (ClientTableRowModel | CompanyWithClients) & CustomFieldsType,
      ): item is ClientTableRowModel & CustomFieldsType => {
        return item && typeof item === 'object' && 'clientFullData' in item;
      };

      const isCompanyWithClients = (
        item: (ClientTableRowModel | CompanyWithClients) & CustomFieldsType,
      ): item is CompanyWithClients & CustomFieldsType => {
        return (
          item &&
          typeof item === 'object' &&
          'clients' in item &&
          'fields' in item
        );
      };

      const getClientOrCompanyData = () => {
        if (isClientTableRowModel(data)) {
          return {
            updatingClient: data.clientFullData,
            updatingCompany: undefined,
          };
        } else if (isCompanyWithClients(data)) {
          return {
            updatingClient: undefined,
            updatingCompany: data,
          };
        }

        // Fallback - this should not happen in normal operation
        console.warn('Unknown data type in CustomFieldsRenderer:', data);
        return { updatingClient: undefined, updatingCompany: undefined };
      };

      const handleUpdateCustomFieldValue = (newValue: string) => {
        if (!field.id) {
          return;
        }
        const value = newValue?.trim();
        // if both values are falsey or
        // if value has not changed then nothing to update
        const skipUpdateCall = (!value && !cellValue) || value === cellValue;
        if (!skipUpdateCall) {
          const { updatingClient, updatingCompany } = getClientOrCompanyData();

          onCustomFieldValueUpdated({
            fieldId: field.id,
            value,
            updatingClient,
            updatingCompany,
          });
        }
        // hide quick text editor
        setEditable(false);
        setIsEdit(false);
      };

      const handleComplexCustomFieldValueChange = (
        customFieldValueData: CustomFieldValueData,
      ) => {
        const { fieldId, value } = customFieldValueData;
        const { updatingClient, updatingCompany } = getClientOrCompanyData();

        onCustomFieldValueUpdated({
          fieldId,
          value,
          updatingClient,
          updatingCompany,
        });
      };

      const inputActions = React.useMemo(() => {
        let actions: QuickTextEditorProps['inputActions'] = undefined;
        if (field.type === 'url') {
          actions = [
            {
              key: 'external-link-action',
              icon: ExternalLinkIcon,
              onClick: (
                _: React.MouseEvent<HTMLButtonElement>,
                linkUrl: string,
              ) => window.open(UrlUtils.GetFullUrl(linkUrl), '_blank'),
            },
          ];
        }

        return actions;
      }, [field.type]);

      /**
       * This function is used to switch custom field cell
       * to edit mode.
       */
      const setEditableTrue = () => {
        setEditable(true);
        setIsEdit(true);
      };

      /**
       * This renderer function is used to display
       * text field inputs.
       */
      const textCustomFieldRenderer = () => {
        if (editable) {
          return (
            <QuickTextEditor
              onUpdate={handleUpdateCustomFieldValue}
              canEdit={editable}
              onCancel={() => {
                setEditable(false);
                setIsEdit(false);
              }}
              onFocus={(ev) => {
                if (!ev) setEditable(false);
              }}
              // cellValue might not be string, if not we pass empty string value for processing
              value={typeof cellValue === 'string' ? cellValue : ''}
              showCopyIcon
              InputComponent={inputComponent}
              skipErrorOnUnfocused
              inputWrapperStyleProps={{
                width: 200,
                position: 'absolute',
                zIndex: 1,
              }}
              validator={(value: string) =>
                getFieldValidationByType(field.type, value)
              }
              inputActions={inputActions}
              canSubmitEmptyString
            />
          );
        }

        if (cellValue) {
          return (
            <div
              onClick={setEditableTrue}
              className="w-full p-1.5 rounded hover:cursor-pointer hover:bg-[#EEF0F3]"
            >
              <Body size="sm" className="overflow-hidden text-ellipsis ">
                {formatFieldValue(
                  field.type,
                  typeof cellValue === 'string' ? cellValue : '',
                )}
              </Body>
            </div>
          );
        }

        return null;
      };

      /**
       * This renderer is used to display multi select field
       * input.
       */
      const multiSelectCustomFieldRenderer = () => {
        if (editable) {
          return (
            <MultiSelect
              autoFocus={!isMobile}
              showPreviewPlaceholder={false}
              tagsLimit={1}
              shouldOpenMenu={editable}
              isCellContext
              onClose={() => {
                setEditable(false);
                setIsEdit(false);
              }}
              placeholder="Add a tag"
              initialFieldValue={Array.isArray(cellValue) ? cellValue : []}
              field={
                isMultiSelectCustomField(field)
                  ? field
                  : (field as MultiSelectCustomField)
              }
              onSaveProperty={onCustomFieldPropertyUpdated}
              onSaveValue={handleComplexCustomFieldValueChange}
            />
          );
        }

        if (selectedTags.length > 0) {
          return (
            <div
              onClick={setEditableTrue}
              className="w-full p-1.5 rounded hover:cursor-pointer hover:bg-[#EEF0F3]"
            >
              <MultiSelectInput selectedTags={selectedTags} isCellContext />
            </div>
          );
        }

        return null;
      };

      const addressCustomFieldRenderer = () => {
        if (editable) {
          return (
            <AddressInput
              field={
                isAddressCustomField(field)
                  ? field
                  : (field as AddressCustomField)
              }
              value={
                cellValue && isAddressCustomFieldValues(cellValue)
                  ? cellValue
                  : { fullAddress: '' }
              }
              placeholder={
                MapFieldTypeToDefaultProps[field.type]?.placeholder || ''
              }
              onSaveValue={handleComplexCustomFieldValueChange}
              onClose={() => {
                setEditable(false);
                setIsEdit(false);
              }}
              renderAsCell
              shouldOpenMenu={editable}
            />
          );
        }

        const addressText =
          cellValue && isAddressCustomFieldValues(cellValue)
            ? cellValue.fullAddress
            : '';

        if (addressText) {
          return (
            <div
              onClick={setEditableTrue}
              className="w-full p-1.5 rounded hover:cursor-pointer hover:bg-[#EEF0F3]"
            >
              <Body size="sm" className="overflow-hidden text-ellipsis">
                {addressText}
              </Body>
            </div>
          );
        }

        return null;
      };

      const hasUnresolvedMultiSelectTags =
        field.type === 'multiSelect' &&
        multiSelectOptionIds.length > 0 &&
        selectedTags.length === 0;

      const isCellValueEmpty =
        cellValue === null ||
        cellValue === undefined ||
        ((typeof cellValue === 'string' || Array.isArray(cellValue)) &&
          cellValue.length === 0) ||
        (typeof cellValue === 'object' &&
          Object.keys(cellValue).length === 0) ||
        (field.type === 'address' &&
          isAddressCustomFieldValues(cellValue) &&
          (cellValue.fullAddress === '' || cellValue.fullAddress === null));
      const shouldShowFieldIcon =
        !editable && (isCellValueEmpty || hasUnresolvedMultiSelectTags);

      return (
        <div className="flex items-center">
          {shouldShowFieldIcon && (
            <IconButton
              onClickCapture={setEditableTrue}
              className="hover:cursor-pointer hover:bg-[#EEF0F3] [&_svg]:text-black-heading [&_svg]:text-[14px] [&_svg]:fill-none"
            >
              <FieldIcon />
            </IconButton>
          )}
          {COPYABLE_FIELDS_TYPES.includes(field.type) &&
            textCustomFieldRenderer()}
          {field.type === 'multiSelect' && multiSelectCustomFieldRenderer()}
          {field.type === 'address' && addressCustomFieldRenderer()}
        </div>
      );
    },
  );
